import { useState, useEffect, useCallback } from "react";

const C = {
  bg:           "#060608",
  surface:      "#0d0d14",
  surfaceHover: "#111118",
  border:       "#ffffff10",
  borderHover:  "#ffffff20",
  accent:       "#00e5a0",
  accentDim:    "#00e5a014",
  accentBorder: "#00e5a035",
  accentGlow:   "#00e5a040",
  text:         "#f2f2fa",
  muted:        "#8888aa",
  error:        "#ff6b6b",
  font:         "'Syne', sans-serif",
};

const BACKEND = "https://melvo-backend-production.up.railway.app";
const MIN_SELECTED = 3;

// ─── Loading animation ────────────────────────────────────────────────────────
function GeneratingState({ businessName }) {
  const [dots, setDots] = useState(0);
  const [phase, setPhase] = useState(0);

  const phases = [
    "Reading your answers",
    "Learning your voice",
    "Drafting first templates",
    "Refining the tone",
    "Finalizing 20 templates",
  ];

  useEffect(() => {
    const d = setInterval(() => setDots(n => (n + 1) % 4), 400);
    const p = setInterval(() => setPhase(n => Math.min(n + 1, phases.length - 1)), 2200);
    return () => { clearInterval(d); clearInterval(p); };
  }, [phases.length]);

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      minHeight: 360, gap: 32, textAlign: "center",
      fontFamily: C.font,
    }}>
      <style>{`
        @keyframes spin-ring {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; transform: scale(0.95); }
          50%       { opacity: 1;   transform: scale(1.05); }
        }
      `}</style>

      {/* Spinner */}
      <div style={{ position: "relative", width: 72, height: 72 }}>
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: `2px solid ${C.accentBorder}`,
        }} />
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: `2px solid transparent`,
          borderTopColor: C.accent,
          animation: "spin-ring 0.9s linear infinite",
        }} />
        <div style={{
          position: "absolute", inset: 10, borderRadius: "50%",
          background: C.accentDim,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22,
          animation: "pulse-glow 1.8s ease-in-out infinite",
        }}>✦</div>
      </div>

      <div>
        <h3 style={{ color: C.text, fontSize: 20, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.02em" }}>
          Writing templates for {businessName || "your business"}{".".repeat(dots)}
        </h3>
        <p style={{
          color: C.accent, fontSize: 13, fontWeight: 500,
          transition: "opacity 0.4s",
        }}>
          {phases[phase]}
        </p>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {phases.map((_, i) => (
          <div key={i} style={{
            width: i <= phase ? 20 : 6, height: 6, borderRadius: 3,
            background: i <= phase ? C.accent : "#ffffff12",
            transition: "all 0.4s ease",
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── Template card ────────────────────────────────────────────────────────────
function TemplateCard({ text, index, selected, onToggle }) {
  const charCount = text.length;
  const overLimit = charCount > 160;

  return (
    <button
      type="button"
      onClick={() => onToggle(index)}
      style={{
        background: selected ? C.accentDim : C.surface,
        border: `1.5px solid ${selected ? C.accent : overLimit ? C.error + "50" : C.border}`,
        borderRadius: 14,
        padding: "16px",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.18s ease",
        transform: selected ? "scale(1.01)" : "scale(1)",
        boxShadow: selected ? `0 0 20px ${C.accentGlow}` : "none",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        fontFamily: C.font,
      }}
      onMouseEnter={e => {
        if (!selected) {
          e.currentTarget.style.borderColor = C.borderHover;
          e.currentTarget.style.background = C.surfaceHover;
        }
      }}
      onMouseLeave={e => {
        if (!selected) {
          e.currentTarget.style.borderColor = overLimit ? C.error + "50" : C.border;
          e.currentTarget.style.background = C.surface;
        }
      }}
    >
      {/* Selection badge */}
      <div style={{
        position: "absolute", top: 12, right: 12,
        width: 20, height: 20, borderRadius: "50%",
        background: selected ? C.accent : "#ffffff0a",
        border: `1.5px solid ${selected ? C.accent : "#ffffff18"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, color: selected ? C.bg : C.muted,
        fontWeight: 800, transition: "all 0.18s",
        flexShrink: 0,
      }}>
        {selected ? "✓" : index + 1}
      </div>

      {/* Template text */}
      <p style={{
        color: selected ? C.text : "#d0d0e8",
        fontSize: 13,
        lineHeight: 1.65,
        margin: 0,
        paddingRight: 28,
        fontWeight: 400,
        letterSpacing: "0.005em",
      }}>
        {text}
      </p>

      {/* Char count */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 2,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 500,
          color: overLimit ? C.error : charCount > 140 ? "#f5a623" : C.muted,
        }}>
          {charCount} / 160 chars {overLimit ? "⚠" : ""}
        </span>
        {selected && (
          <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>
            Active ✦
          </span>
        )}
      </div>
    </button>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────
function ErrorState({ message, onRetry }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      minHeight: 300, gap: 20, textAlign: "center",
      fontFamily: C.font,
    }}>
      <div style={{ fontSize: 40 }}>⚠</div>
      <div>
        <h3 style={{ color: C.text, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          Template generation failed
        </h3>
        <p style={{ color: C.muted, fontSize: 14, maxWidth: 360 }}>{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          background: C.accentDim, border: `1.5px solid ${C.accentBorder}`,
          borderRadius: 10, padding: "10px 24px",
          color: C.accent, fontSize: 14, fontWeight: 600,
          cursor: "pointer", fontFamily: C.font,
        }}
      >
        Try again
      </button>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * TemplatePickerStep
 * Props:
 *   data        — questionnaire answers object from QuestionnaireStep
 *   onChange    — (updatedData) => void  — called with { templates, selectedTemplates }
 *   onComplete  — () => void  — called when user confirms selection
 */
export default function TemplatePickerStep({ data = {}, onChange, onComplete }) {
  const [status, setStatus]       = useState("idle");   // idle | loading | done | error
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected]   = useState(new Set());
  const [errorMsg, setErrorMsg]   = useState("");

  const generate = useCallback(async () => {
    setStatus("loading");
    setSelected(new Set());

    try {
      const res = await fetch(`${BACKEND}/onboarding/generate-templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessName:    data.business_info      || "",
          businessType:    data.business_type      || "",
          cityState:       data.city_state         || "",
          typicalVisit:    data.typical_visit      || "",
          tone:            data.tone               || "casual",
          yearsInBusiness: data.years_in_business  || "1_to_3",
          differentiator:  data.differentiator     || "",
          customerBase:    data.customer_base      || "mixed",
          customerFeeling: data.customer_feeling   || "taken_care_of",
          avoidPhrases:    data.avoid_phrases      || "",
          brandFeeling:    data.brand_feeling      || "",
        }),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const json = await res.json();
      if (!Array.isArray(json.templates) || json.templates.length === 0) {
        throw new Error("No templates returned — please try again.");
      }

      setTemplates(json.templates);
      setStatus("done");
      onChange?.({ ...data, templates: json.templates, selectedTemplates: [] });

    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Check your connection and try again.");
      setStatus("error");
    }
  }, [data, onChange]);

  // Auto-fetch when mounted (or when questionnaire data changes)
  useEffect(() => {
    if (data.templates) {
      setTemplates(data.templates);
      setSelected(new Set(data.selectedTemplates || []));
      setStatus("done");
    } else {
      generate();
    }
  }, [data.templates, data.selectedTemplates, generate]);

  const toggleTemplate = (index) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      const selectedTemplates = [...next];
      onChange?.({ ...data, templates, selectedTemplates });
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size < MIN_SELECTED) return;
    onComplete?.();
  };

  const businessName = (data.business_info || "").split(/[—\-–]/)[0].trim();

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", fontFamily: C.font }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        ::selection { background: #00e5a022; color: #00e5a0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1a1a2e; border-radius: 2px; }
      `}</style>

      {status === "loading" && <GeneratingState businessName={businessName} />}
      {status === "error"   && <ErrorState message={errorMsg} onRetry={generate} />}

      {status === "done" && (
        <>
          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "5px 11px", borderRadius: 20,
              background: C.accentDim, border: `1px solid ${C.accentBorder}`,
              marginBottom: 14,
            }}>
              <span style={{ color: C.accent, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                04
              </span>
              <span style={{ width: 1, height: 9, background: C.accentBorder }} />
              <span style={{ color: C.accent, fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Your templates
              </span>
            </div>

            <h2 style={{
              color: C.text, fontSize: "clamp(18px, 3.5vw, 24px)",
              fontWeight: 700, letterSpacing: "-0.025em",
              lineHeight: 1.3, marginBottom: 8,
            }}>
              Pick your favorites
            </h2>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.6 }}>
              These were written in your voice. Select at least {MIN_SELECTED} — they'll rotate so customers never see the same message twice.
            </p>
          </div>

          {/* Selection status bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 14px",
            background: selected.size >= MIN_SELECTED ? C.accentDim : "#ffffff06",
            border: `1px solid ${selected.size >= MIN_SELECTED ? C.accentBorder : C.border}`,
            borderRadius: 10,
            marginBottom: 20,
            transition: "all 0.3s ease",
          }}>
            <span style={{
              fontSize: 13, fontWeight: 600,
              color: selected.size >= MIN_SELECTED ? C.accent : C.muted,
            }}>
              {selected.size === 0
                ? `Select at least ${MIN_SELECTED} templates`
                : `${selected.size} selected${selected.size < MIN_SELECTED ? ` — ${MIN_SELECTED - selected.size} more to go` : " ✓ ready to save"}`
              }
            </span>
            <button
              type="button"
              onClick={generate}
              style={{
                background: "transparent", border: "none",
                color: C.muted, fontSize: 12, fontWeight: 500,
                cursor: "pointer", fontFamily: C.font,
                textDecoration: "underline",
              }}
            >
              Regenerate
            </button>
          </div>

          {/* Template grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
            marginBottom: 28,
            maxHeight: "52vh",
            overflowY: "auto",
            paddingRight: 4,
          }}>
            {templates.map((text, i) => (
              <TemplateCard
                key={i}
                text={text}
                index={i}
                selected={selected.has(i)}
                onToggle={toggleTemplate}
              />
            ))}
          </div>

          {/* Confirm button */}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selected.size < MIN_SELECTED}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: selected.size >= MIN_SELECTED
                ? "linear-gradient(135deg, #00e5a0, #00c48a)"
                : "#ffffff08",
              border: "none",
              borderRadius: 12, padding: "14px 24px",
              color: selected.size >= MIN_SELECTED ? C.bg : "#ffffff20",
              fontSize: 15, fontWeight: 700,
              cursor: selected.size >= MIN_SELECTED ? "pointer" : "default",
              fontFamily: C.font,
              letterSpacing: "0.01em",
              transition: "all 0.2s",
              boxShadow: selected.size >= MIN_SELECTED ? `0 4px 20px ${C.accentGlow}` : "none",
            }}
            onMouseEnter={e => {
              if (selected.size >= MIN_SELECTED) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 28px #00e5a055";
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = selected.size >= MIN_SELECTED ? `0 4px 20px ${C.accentGlow}` : "none";
            }}
          >
            Save {selected.size >= MIN_SELECTED ? selected.size : ""} templates & go live →
          </button>
        </>
      )}
    </div>
  );
}
